/**
 * Tiny pub-sub store for the model editor's "node trace" highlight.
 *
 * `model_view` computes which node is focused and which other nodes are
 * connected to it (both upstream through inputs AND downstream through
 * outputs) when the user clicks a node, and writes the result here. Each
 * rendered `NodeComponent` subscribes and re-renders with a state class
 * (`node--focus`, `node--chain`, or `node--faded`) so the highlight survives
 * the React render plugin's own re-renders.
 *
 * Connections are rendered by Rete core (not React), so `model_view` still
 * mutates their DOM classes directly.
 */
export interface NodeFocusState {
  focusedId: number | null
  /** All node ids in the connected chain — both upstream and downstream of
   * the focused node. The focused node itself is also included. */
  chainIds: Set<number>
}

type Listener = () => void

let state: NodeFocusState = { focusedId: null, chainIds: new Set<number>() }
const listeners = new Set<Listener>()

/**
 * Id of the node the AEGIS chat is currently "pointing at" — set while the
 * user hovers a `#id` reference link in a chat message so the matching node
 * lights up on the canvas. Independent of the click-driven focus trace
 * above so the two highlights can coexist without clobbering each other.
 */
let agentHighlightId: number | null = null

function notify(): void {
  listeners.forEach(l => {
    try {
      l()
    } catch (_err) {
      // Listener errors must not abort the rest of the subscribers.
    }
  })
}

export function getNodeFocusState(): NodeFocusState {
  return state
}

export function setNodeFocusState(next: NodeFocusState): void {
  state = next
  notify()
}

export function subscribeNodeFocus(callback: Listener): () => void {
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
  }
}

/** Current id the chat is highlighting, or `null` when nothing is hovered. */
export function getAgentHighlightId(): number | null {
  return agentHighlightId
}

/**
 * Highlight (or clear) the node referenced from the chat. Pass `null` to
 * clear. Shares the focus-store listener set so every mounted
 * `NodeComponent` re-renders and the highlight class flows into `className`.
 */
export function setAgentHighlight(nodeId: number | null): void {
  if (agentHighlightId === nodeId) return
  agentHighlightId = nodeId
  notify()
}

/**
 * Returns the appropriate highlight class for the given node id, or `""` if
 * no node is currently focused (i.e. the trace overlay is off).
 */
export function classForNode(nodeId: number): string {
  if (state.focusedId === null) return ""
  if (state.focusedId === nodeId) return "node--focus"
  if (state.chainIds.has(nodeId)) return "node--chain"
  return "node--faded"
}

/**
 * Returns the chat-highlight class for the given node id (`node--agent-ref`)
 * when the chat is pointing at it, otherwise `""`. Independent of
 * `classForNode` so a node can be both in-chain AND chat-highlighted.
 */
export function agentHighlightClassForNode(nodeId: number): string {
  return agentHighlightId === nodeId ? "node--agent-ref" : ""
}
